import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '../../ui/button';
import {
    addEquipment,
    removeEquipment,
    updateEquipment,
    EquipmentDTO,
} from '../../../services/apiService';
import { useToast } from '../../../hooks/use-toast';
import EquipmentItemCard from '../equipment/EquipmentItemCard';
import EquipmentItemForm from '../equipment/EquipmentItemForm';
import EquipmentTypeFilter from '../equipment/EquipmentTypeFilter';
import RemoveEquipmentDialog from '../equipment/RemoveEquipmentDialog';
import { EquipmentValues, toEquipmentInput } from '../types';

interface EquipmentTabProps {
    machineId: string;
    items: EquipmentDTO[];
    onItemsChange: (items: EquipmentDTO[]) => void;
    readOnly: boolean;
    onChange?: () => void;
}

const toValues = (item: EquipmentDTO): EquipmentValues => ({
    name: item.name,
    type: item.type,
    notes: item.notes ?? '',
    image: item.image ?? null,
    files: item.files,
    policies: item.policies,
});

export default function EquipmentTab({
    machineId,
    items,
    onItemsChange,
    readOnly,
    onChange,
}: EquipmentTabProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [editingId, setEditingId] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const [removing, setRemoving] = useState<EquipmentDTO | null>(null);

    const visible = typeFilter
        ? items.filter((item) => item.type === typeFilter)
        : items;

    const commit = (next: EquipmentDTO[]) => {
        onItemsChange(next);
        onChange?.();
    };

    const fail = (error: unknown) => {
        console.error('Error saving equipment:', error);
        toast({
            title: t('roomOverview.equipment.toasts.error'),
            variant: 'destructive',
        });
    };

    const handleAdd = async (values: EquipmentValues) => {
        setIsSaving(true);
        try {
            const created = await addEquipment(
                machineId,
                toEquipmentInput(values)
            );
            commit([...items, created]);
            setAdding(false);
        } catch (error) {
            fail(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdate = async (itemId: string, values: EquipmentValues) => {
        setIsSaving(true);
        try {
            const updated = await updateEquipment(
                machineId,
                itemId,
                toEquipmentInput(values)
            );
            commit(items.map((item) => (item._id === itemId ? updated : item)));
            setEditingId(null);
        } catch (error) {
            fail(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!removing) return;
        const itemId = removing._id;
        try {
            await removeEquipment(machineId, itemId);
            commit(items.filter((item) => item._id !== itemId));
            if (editingId === itemId) setEditingId(null);
        } catch (error) {
            fail(error);
        } finally {
            setRemoving(null);
        }
    };

    return (
        <div className="space-y-4">
            <RemoveEquipmentDialog
                itemName={removing?.name ?? null}
                onConfirm={handleRemove}
                onClose={() => setRemoving(null)}
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
                <EquipmentTypeFilter
                    types={items.map((item) => item.type)}
                    value={typeFilter}
                    onChange={setTypeFilter}
                />
                {!readOnly && (
                    <Button
                        type="button"
                        variant={adding ? 'ghost' : 'outline'}
                        size="sm"
                        className="ml-auto"
                        onClick={() => setAdding((open) => !open)}
                    >
                        {adding ? (
                            t('roomOverview.equipment.close')
                        ) : (
                            <>
                                <Plus
                                    className="mr-1 h-4 w-4"
                                    aria-hidden="true"
                                />
                                {t('roomOverview.equipment.addEquipment')}
                            </>
                        )}
                    </Button>
                )}
            </div>

            {adding && (
                <EquipmentItemForm
                    isSaving={isSaving}
                    onSave={handleAdd}
                    onCancel={() => setAdding(false)}
                />
            )}

            {visible.length === 0 && !adding ? (
                <p className="py-8 text-center text-sm font-medium text-subtle-foreground">
                    {t('roomOverview.equipment.empty')}
                </p>
            ) : (
                <div className="space-y-3">
                    {visible.map((item) =>
                        editingId === item._id ? (
                            <EquipmentItemForm
                                key={item._id}
                                initial={toValues(item)}
                                isSaving={isSaving}
                                onSave={(values) =>
                                    handleUpdate(item._id, values)
                                }
                                onCancel={() => setEditingId(null)}
                            />
                        ) : (
                            <EquipmentItemCard
                                key={item._id}
                                item={toValues(item)}
                                onEdit={
                                    readOnly
                                        ? undefined
                                        : () => setEditingId(item._id)
                                }
                                onRemove={
                                    readOnly
                                        ? undefined
                                        : () => setRemoving(item)
                                }
                            />
                        )
                    )}
                </div>
            )}
        </div>
    );
}
